import React, { useEffect, useState } from "react";
import { EChartsRenderer } from 'components/charts';
import PropTypes from "prop-types";
import moment from "moment";
import { NotificationInline } from "components/notifications";
import { AssetService } from "services";
import { FormHeading } from "components/form-fields";
import { Key } from "components/key";
import { ReportList } from "components/reporting";
import './SpeedDataTab.css';
import { GetFilesButton } from "components/vehicle/GetFilesButton";

const defaultArray = [];
const defaultObject = {};
const secondsInMinute = 60;

// The key swatches in SpeedDataTab.css copy these patterns and colours; change both together.
const stripeThickness = 2;
const stripeGap = 3;
const stripeRotation = Math.PI / 4;
const dotCellSize = 2;
const dotGap = 2;
const dotFill = 0.9;

const partlyRecordedDecal = {
    symbol: 'rect',
    symbolSize: 1,
    dashArrayX: [1, 0],
    dashArrayY: [stripeThickness, stripeGap],
    rotation: stripeRotation,
    color: 'rgba(255, 255, 255, 0.6)'
};
const recordsDisagreeDecal = {
    symbol: 'circle',
    symbolSize: dotFill,
    dashArrayX: [dotCellSize, dotGap],
    dashArrayY: [dotCellSize, dotGap],
    color: 'rgba(255, 255, 255, 0.7)'
};

// A contested minute has two records holding a reading for the same second, so its records disagree.
// Any other minute with fewer than 60 recorded seconds is partly recorded. A missing count is treated
// as complete and a missing contested flag as uncontested.
function describeReading(speedDataForMinute) {
    const recordedSeconds = speedDataForMinute.recordedSeconds == null ? secondsInMinute : speedDataForMinute.recordedSeconds;
    const recordsDisagree = speedDataForMinute.contested === true;

    return {
        min: speedDataForMinute.min,
        max: speedDataForMinute.max,
        average: speedDataForMinute.average,
        recordedSeconds: recordedSeconds,
        partlyRecorded: !recordsDisagree && recordedSeconds < secondsInMinute,
        recordsDisagree: recordsDisagree
    };
}

function toSeriesItem(item) {
    const seriesItem = {
        value: item.value,
        itemStyle: {
            color: item.colour,
            borderColor: item.colour
        }
    };

    if (item.decal != null) {
        seriesItem.itemStyle.decal = item.decal;
    }

    if (item.reading != null) {
        seriesItem.reading = item.reading;
    }

    return seriesItem;
}

function buildEmptyMinuteBars() {
    return {
        min: { value: 0 },
        beforeAverage: { value: 0 },
        averageMarker: { value: 0 },
        afterAverage: { value: 0 },
        partlyRecorded: false,
        recordsDisagree: false
    };
}

// Builds the four stacked bars for a minute with readings: min speed, min to average, the average
// marker and average to max. The flags say which hatch, if any, the minute is drawn with.
function buildMinuteBars(speedDataForMinute, barColour) {
    const reading = describeReading(speedDataForMinute);
    let decal = null;
    let beforeAverage = speedDataForMinute.average - speedDataForMinute.min - 1;
    let averageMarker = 1;
    let afterAverage = speedDataForMinute.max - speedDataForMinute.average;

    if (reading.recordsDisagree) {
        // No average is marked because the records disagree on it.
        decal = recordsDisagreeDecal;
        beforeAverage = speedDataForMinute.max - speedDataForMinute.min;
        averageMarker = 0;
        afterAverage = 0;
    }
    else if (reading.partlyRecorded) {
        decal = partlyRecordedDecal;
    }

    return {
        min: {
            value: speedDataForMinute.min,
            colour: barColour,
            decal: decal,
            reading: reading
        },
        beforeAverage: {
            value: beforeAverage,
            colour: barColour,
            decal: decal
        },
        averageMarker: {
            value: averageMarker,
            colour: '#66BAFB'
        },
        afterAverage: {
            value: afterAverage,
            colour: barColour,
            decal: decal
        },
        partlyRecorded: reading.partlyRecorded,
        recordsDisagree: reading.recordsDisagree
    };
}

export function SpeedDataTab(
    {
        assetId = 0,
        date = PropTypes.string,
        harshBrakingData = defaultArray,
        tachoData = defaultObject,
        isHarshBreakingLoading = true,
        isHarshBrakingLoading
    }
) {
    const periodSelectorDateFormat = "DD MMM YYYY";
    const minutesInDay = 1440;
    const minutesInHour = 60;
    const halfWay = 50;
    const graphKey = [
        { id: 1, name: "Top speed per minute", colours: "#0C2235" },
        { id: 2, name: "Harsh braking occurred", colours: "#F0578A" },
        { id: 3, name: "Average speed per minute", icon: "minus", colours: "#FFFFFF", iconColour: "#66BAFF", hideBorder: true },
        { id: 4, name: "Authorised speed", icon: "minus", colours: "#FFFFFF", iconColour: "#BDC6FA", hideBorder: true }
    ]
    const partlyRecordedKeyItem = { id: 5, name: "Minute partly recorded", swatch: (<div className="speed-key-swatch speed-key-swatch-partly-recorded" />) };
    const recordsDisagreeKeyItem = { id: 6, name: "Records disagree", swatch: (<div className="speed-key-swatch speed-key-swatch-records-disagree" />) };
    const [dates, setDates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isErrored, setIsErrored] = useState(false);
    const [sliderStartPoint, setSliderStartPoint] = useState(halfWay);
    const [reportDownloading, setReportDownloading] = useState(false);
    const [graphData, setGraphData] = useState({ stack1: [], stack2: [], stack3: [], stack4: [], line1: [] });
    const [uploadList, setUploadList] = useState([]);
    const [hasPartlyRecordedMinute, setHasPartlyRecordedMinute] = useState(false);
    const [hasRecordsDisagreeMinute, setHasRecordsDisagreeMinute] = useState(false);

    useEffect(() => {
        if (assetId && date && harshBrakingData && isHarshBrakingLoading === false) {
            getSpeedData();
        }
        else if (isHarshBrakingLoading === false && !harshBrakingData) {
            setIsErrored(true);
        }
    }, [assetId, date, harshBrakingData, isHarshBrakingLoading]);

    useEffect(() => {
        if (tachoData && tachoData.uploads) {
            setUploadList(tachoData.uploads);
        }
    }, [tachoData]);

    function daylightSavingHarshBrakingConvert(utcDate) {
        var dateUtcMoment = moment.utc(utcDate);
        var convertedDate = dateUtcMoment.toDate();
        return moment(convertedDate).local().format("HH:mm");
    }

    function formatTooltip(params) {
        const heading = `<b>${moment(date).format(periodSelectorDateFormat)}</b> ${params[0].name}`;
        const reading = params[0].data.reading;
        let body;

        if (reading == null) {
            body = `Range: 0 - 0 Kph <br />Average: 0 Kph`;
        }
        else if (reading.recordsDisagree) {
            body = `Range: ${reading.min} - ${reading.max} Kph <br />Records disagree for part of this minute`;
        }
        else if (reading.partlyRecorded) {
            body = `Range: ${reading.min} - ${reading.max} Kph <br />Average: ${reading.average} Kph <br />${reading.recordedSeconds} of ${secondsInMinute} seconds recorded`;
        }
        else {
            body = `Range: ${reading.min} - ${reading.max} Kph <br />Average: ${reading.average} Kph`;
        }

        return `${heading} <br />${body}`;
    }

    function getSpeedData() {
        setIsLoading(true);
        let tempDates = [];
        let tempMinSpeedData = [];
        let tempBeforeSpeedData = [];
        let tempAverageSpeedData = [];
        let tempAfterSpeedData = [];
        let authorisedSpeed = [];
        let isSliderSet = false;
        let anyPartlyRecorded = false;
        let anyRecordsDisagree = false;

        let harshBrakingTimes = harshBrakingData.map(x => daylightSavingHarshBrakingConvert(x.startDate));
        AssetService.getSpeedDataForAsset(assetId, moment(date).format("YYYY-MM-DD")).then((response) => {
            if (response != null) {
                for (let hour = 0; hour < 24; hour++) {
                    for (let minute = 0; minute < 60; minute++) {
                        let isHarshBrakingForTime = false;
                        let formattedHour = hour < 10 ? `0${hour}` : hour;
                        let formattedMinute = minute < 10 ? `0${minute}` : minute;
                        let formattedTime = `${formattedHour}:${formattedMinute}`;
                        tempDates.push(formattedTime);

                        let speedDataForMinute = response.speedData.find(x => x.hour === hour && x.minute === minute);

                        if (harshBrakingTimes.includes(formattedTime)) {
                            isHarshBrakingForTime = true;
                        }

                        let minuteBars = buildEmptyMinuteBars();

                        if (speedDataForMinute) {
                            if (!isSliderSet) {
                                let totalMinutes = (hour * minutesInHour) + minute;
                                setSliderStartPoint(totalMinutes / minutesInDay * 100);
                                isSliderSet = true;
                            }

                            const barColour = isHarshBrakingForTime ? '#F0578A' : '#0C2235';
                            minuteBars = buildMinuteBars(speedDataForMinute, barColour);
                        }

                        tempMinSpeedData.push(minuteBars.min);
                        tempBeforeSpeedData.push(minuteBars.beforeAverage);
                        tempAverageSpeedData.push(minuteBars.averageMarker);
                        tempAfterSpeedData.push(minuteBars.afterAverage);
                        anyPartlyRecorded = anyPartlyRecorded || minuteBars.partlyRecorded;
                        anyRecordsDisagree = anyRecordsDisagree || minuteBars.recordsDisagree;
                        authorisedSpeed.push(response.authorisedSpeed);
                    }
                }

                setDates(tempDates);
                setGraphData({
                    stack1: tempMinSpeedData,
                    stack2: tempBeforeSpeedData,
                    stack3: tempAverageSpeedData,
                    stack4: tempAfterSpeedData,
                    line1: authorisedSpeed
                });
                setHasPartlyRecordedMinute(anyPartlyRecorded);
                setHasRecordsDisagreeMinute(anyRecordsDisagree);
            }

        })
            .catch(() => {
                setIsErrored(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function onReportDownloading(isDownloading) {
        setReportDownloading(isDownloading);
    }

    // The hatch entries are listed only when the loaded day contains a minute drawn with that hatch.
    const keyItems = [...graphKey];

    if (hasPartlyRecordedMinute) {
        keyItems.push(partlyRecordedKeyItem);
    }

    if (hasRecordsDisagreeMinute) {
        keyItems.push(recordsDisagreeKeyItem);
    }

    return (
        <div>
            <div>
                <div className="inline-items spacer-bottom">
                    <ReportList lastSearchPayload={{ filters: [{ "key": "assetId", "value": assetId }], "startDate": date, "endDate": date }} pageName="DayDetail-Speed" popoverUsePortal={false} onReportDownloadingChange={onReportDownloading} disabled={isLoading} />
                    <GetFilesButton uploadsList={uploadList} disabled={isLoading} />
                </div>
                <NotificationInline intent="success" text="Your report has been queued, and will download as soon as it's ready." show={reportDownloading} />
            </div>
            <NotificationInline intent="danger" text="Error: Speed data for this day has failed to load. Please refresh the page" show={isErrored} />
            <div className="graph-size-outer">
                <EChartsRenderer showLoading={isLoading} className="graph-size-inner" option={{
                    grid: {
                        left: 35,
                        top: 35,
                        right: 35,
                        bottom: 70,
                        tooltip: {
                            show: true
                        }
                    },
                    tooltip: {
                        trigger: 'axis',
                        position: function (pt) {
                            return [pt[0], '10%'];
                        },
                        formatter: formatTooltip
                    },
                    xAxis: {
                        type: 'category',
                        boundaryGap: false,
                        data: dates,
                        axisLabel: {
                            fontFamily: 'Open Sans'
                        }
                    },
                    yAxis: {
                        type: 'value',
                        boundaryGap: [0, '100%'],
                        name: 'Speed (Kph)',
                        min: 0,
                        max: 120,
                        axisLabel: {
                            fontFamily: 'Open Sans'
                        }
                    },
                    dataZoom: [
                        {
                            type: 'inside',
                            start: sliderStartPoint,
                            end: sliderStartPoint + 10,
                        },
                        {
                            type: 'slider',
                            start: sliderStartPoint,
                            end: sliderStartPoint + 10,
                        }
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'x',
                            data: graphData.stack1.map(toSeriesItem),
                        },
                        {
                            type: 'bar',
                            stack: 'x',
                            data: graphData.stack2.map(toSeriesItem),
                        },
                        {
                            type: 'bar',
                            stack: 'x',
                            data: graphData.stack3.map(toSeriesItem),
                        },
                        {
                            type: 'bar',
                            stack: 'x',
                            data: graphData.stack4.map(toSeriesItem),
                        },
                        {
                            type: 'line',
                            data: graphData.line1,
                            showSymbol: false,
                            itemStyle: {
                                color: '#BDC6FA'
                            }
                        }
                    ]
                }} />
            </div>
            <div className="key-bottom">
                <FormHeading headingLevel="h3">Key</FormHeading>
                <Key hasPadding={false} loading={isLoading} items={keyItems} />
            </div>
        </div>
    );
}

SpeedDataTab.propTypes = {
    assetId: PropTypes.number,
    date: PropTypes.string,
    harshBrakingData: PropTypes.array,
    tachoData: PropTypes.object,
    isHarshBrakingLoading: PropTypes.bool
};